import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getIssueImage } from '../../api/aws/s3';
import { companiesRef } from '../../api/firebase/firestore/company';
import {
  addFavoriteIssue,
  deleteFavoriteIssue,
  favoriteIssuesRef
} from '../../api/firebase/firestore/issue';
import noImage from '../../components/assets/noImage.svg';
import { black, blueGrayText, gray } from '../../constants/Colors';
import { buttonFont } from '../../constants/Location';
import { budget, genreList, purpose } from '../../constants/Select';
import { select } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomSnackbar from './CustomSnackbar';
import PublisherLikeButton from './PublisherLikeButton';

import type { Issue } from '../../api/firebase/firestore/issue/issueType';

interface Props {
  issue: Issue;
}

const styles = {
  buttonFont,
  buttons: {
    '@media screen and (max-width:480px)': {
      justifyContent: 'space-between'
    },
    columnGap: '16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  cardWrapper: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    width: '100%'
  },
  cardcontent: {
    '&:last-child': {
      paddingBottom: 0
    },
    padding: {
      md: '32px',
      xs: '16px'
    }
  },
  category: {
    color: blueGrayText,
    fontSize: '14px'
  },
  categoryTitle: {
    '@media screen and (max-width:480px)': { fontSize: '18px' },
    color: black,
    fontSize: '20px',
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  checkMark: {
    color: '#0A7EBF',
    height: '56px',
    marginTop: '-50px',
    width: '56px'
  },
  checkbox: {
    '&.Mui-checked': {
      color: black
    },
    color: black
  },
  closeButton: {
    '@media screen and (max-width:480px)': {
      width: '96px'
    },
    borderColor: 'black',
    color: 'black',
    marginRight: '12px',
    width: '124px'
  },
  comparison: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'row',
      height: '40px',
      justifyContent: 'flex-end',
      padding: '8px 16px',
      width: '100%'
    },
    alignItems: 'center',
    bgcolor: '#DBDBE0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '48px'
  },
  confirmButton: {
    '@media screen and (max-width:480px)': {
      width: '199px'
    },
    width: '260px'
  },
  container: {
    alignItems: 'center',
    display: 'flex'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '8px',
    marginTop: '8px'
  },
  favoriteButton: {
    '&:hover': {
      backgroundColor: '#F2F2F2',
      boxShadow: 'none',
      opacity: '70%'
    },
    backgroundColor: '#F2F2F2',
    boxShadow: 'none',
    height: {
      xs: '36px'
    },
    marginTop: {
      md: '24px',
      sm: '16px',
      xs: '16px'
    },
    width: {
      md: '132px',
      xs: '144px'
    }
  },
  favoriteButtonText: {
    color: black,
    fontSize: '14px',
    ml: '8px'
  },
  generateCheck: {
    '@media screen and (min-width:480px)': { display: 'none' }
  },
  guideMark: {
    color: gray,
    marginLeft: '8px',
    marginTop: '5px',
    width: '18px'
  },
  label: {
    backgroundColor: 'rgba(10, 126, 191, 0.08)',
    border: '1px solid ##0A7EBF',
    borderRadius: '4px',
    height: '24px',
    mb: '12px'
  },
  labelOfAgent: {
    backgroundColor: '#F3F3F3',
    border: '1px solid #757575',
    borderRadius: '4px',
    color: '#757575',
    height: '24px',
    mb: '12px'
  },
  modalButton: {
    marginTop: '24px'
  },
  text: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    overflowWrap: 'anywhere'
  }
};

interface Props {
  issue: Issue;
  cantInquiry: string | undefined;
}

const PublisherIssueCard: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { issue, cantInquiry } = props;
  const [issueId, setIssueId] = useState<string>();
  const [issueImage, setIssueImage] = useState<string>();
  const [companyId, setCompanyId] = useState<string>();

  const getGenre = (value: number | null | undefined): string | undefined => {
    const genreData = genreList()?.find((element: { value: number }) =>
      element.value === value ? element : null
    );
    return genreData?.option;
  };

  const getBudget = (value: number | null | undefined): string | undefined => {
    const budgetData = budget()?.find((element: { value: number }) =>
      element.value === value ? element : null
    );
    return budgetData?.option;
  };

  const getPurpose = (value: number | null | undefined): string | undefined => {
    const purposeData = purpose()?.find((element: { value: number }) =>
      element.value === value ? element : null
    );
    return purposeData?.option;
  };

  const [clientName, setClientName] = useState<string>('');

  const init = async (): Promise<void> => {
    const cId = issue?.companyId;
    const iId = issue?.issueId;
    setIssueId(iId);
    setCompanyId(cId);
    const q = query(companiesRef, where('companyId', '==', cId));
    const companySnap = await getDocs(q);
    const companyData = companySnap.docs?.[0]?.data();
    setClientName(companyData.name as string);
    const image = await getIssueImage(companyData.logoId as string);
    setIssueImage(image);
  };

  useEffect(() => {
    void (async () => {
      await init();
    })();
  }, []);

  const [favorite, setFavorite] = useState<boolean>(false);
  const [issueFavoriteId, setIssueFavoriteId] = useState<string>('');

  useEffect(() => {
    void (async () => {
      try {
        if (currentUser && issueId) {
          const favoriteIssues = favoriteIssuesRef(issueId);
          const q = query(favoriteIssues, where('uid', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          querySnapshot.docs.forEach((document) => {
            setIssueFavoriteId(document.id);
          });
          onSnapshot(q, (snap) => {
            setFavorite(!snap.empty);
          });
        }
      } catch (e) {
        throwError(e);
      }
    })();
  }, [issueId, favorite]);

  const [isFavoriteSubmitting, setIsFavoriteSubmitting] =
    useState<boolean>(false);

  const issueFavoriteHandle = async (iId: string): Promise<void> => {
    try {
      setIsFavoriteSubmitting(true);
      if (currentUser && companyId) {
        if (favorite) {
          await deleteFavoriteIssue(iId, issueFavoriteId);
          setFavorite(false);
        } else {
          await addFavoriteIssue(currentUser.uid, iId, companyId);
          setFavorite(true);
        }
      }
      setIsFavoriteSubmitting(false);
    } catch (e) {
      setIsFavoriteSubmitting(false);
      throwError(e);
    }
  };

  const [open, setOpen] = useState(false);

  const handleSnackbarOpen = (): void => {
    setOpen(true);
  };

  return (
    <Grid sx={styles.cardWrapper}>
      <Grid sx={{ flex: 1 }}>
        <Card
          sx={{
            ':hover': {
              opacity: 0.7
            },
            '@media screen and (max-width:960px)': { display: 'block' },
            border: '1px solid #DBDBE0',
            boxShadow: 'none',
            cursor: 'pointer',
            display: 'flex'
          }}
          onClick={() =>
            issueId && navigate(`/publisher/mypage/issue/${issueId}`)
          }
        >
          <Typography sx={{ width: '30%' }}>
            <Grid
              sx={{
                '@media screen and (max-width:960px)': { display: 'none' },
                display: 'block'
              }}
            >
              <CardContent sx={{ padding: '32px 0 0 32px' }}>
                <Grid container>
                  <Grid item xs={12}>
                    <img
                      src={issueImage ?? noImage}
                      style={{
                        height: 'auto',
                        objectFit: 'contain',
                        width: '100%'
                      }}
                      alt="issueImage"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Grid>
          </Typography>
          <Grid
            sx={{
              '@media screen and (max-width:960px)': { display: 'block' },
              display: 'none'
            }}
          >
            <CardContent sx={styles.cardcontent}>
              <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                  <img
                    src={issueImage ?? noImage}
                    style={{
                      height: 'auto',
                      objectFit: 'contain',
                      width: '100%'
                    }}
                    alt="issueImage"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Grid
                    container
                    columnGap={'8px'}
                    rowGap={'8px'}
                    sx={{ pt: '16px' }}
                  >
                    <Typography>{clientName}</Typography>
                    {issue.agentId && (
                      <Chip
                        sx={styles.labelOfAgent}
                        label={t('issue.agentIssue')}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Grid>
                  <Typography sx={styles.categoryTitle}>
                    {issue.name}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Grid>
          <Grid sx={{ flex: 1 }}>
            <CardContent sx={{ padding: { sm: '32px', xs: '16px' } }}>
              <Typography component="div">
                <Typography
                  component="div"
                  sx={{
                    '@media screen and (max-width:960px)': { display: 'none' },
                    display: 'block'
                  }}
                >
                  <Grid>
                    <Grid container columnGap={'18px'}>
                      <Grid item>{clientName}</Grid>
                      {issue.agentId && (
                        <Chip
                          sx={styles.labelOfAgent}
                          label={t('issue.agentIssue')}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Grid>
                  </Grid>
                  <Typography sx={[styles.categoryTitle, { mt: '16px' }]}>
                    {issue.name}
                  </Typography>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.genre')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      {getGenre(issue.genre)}
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.season')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue.startDate
                          ? dayjs(issue?.startDate?.toDate()).format(
                              'YYYY/MM/DD'
                            )
                          : t('issue.consultation')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.deadline')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue?.applicationDate?.start &&
                          dayjs(issue.applicationDate?.start?.toDate()).format(
                            'YYYY/MM/DD'
                          )}{' '}
                        {issue?.applicationDate?.start && '~'}{' '}
                        {issue?.applicationDate?.end &&
                          dayjs(issue.applicationDate?.end?.toDate()).format(
                            'YYYY/MM/DD'
                          )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.budget')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {getBudget(issue.budget) ?? t('issue.consultation')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Typography component="div">
                  <Grid
                    container
                    sx={{
                      '@media screen and (max-width:600px)': {
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <Grid item sm={12} md={3} sx={styles.container}>
                      <Typography component="div" sx={styles.text}>
                        {t('given.purpose')}
                      </Typography>
                    </Grid>
                    <Grid item sm={12} md={9} sx={styles.text}>
                      <Typography sx={styles.text}>
                        {issue?.purpose && issue?.purpose[0] !== select.other
                          ? getPurpose(issue?.purpose[0])
                          : issue?.purposeOther}
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
                <Divider light sx={styles.divider} />
                <Grid sx={styles.buttons}>
                  <Button
                    sx={styles.favoriteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      issue?.issueId && issueFavoriteHandle(issue.issueId);
                    }}
                    disabled={isFavoriteSubmitting}
                  >
                    {!favorite ? (
                      <StarBorderRoundedIcon
                        sx={{
                          color: blueGrayText,
                          cursor: 'pointer'
                        }}
                      />
                    ) : (
                      <StarRoundedIcon
                        sx={{
                          color: '#FCBA04',
                          cursor: 'pointer'
                        }}
                      />
                    )}
                    <Typography component="span" sx={styles.favoriteButtonText}>
                      {t('given.favoriteIssue')}
                    </Typography>
                  </Button>
                  <Grid
                    sx={{
                      marginTop: {
                        md: '24px',
                        sm: '16px',
                        xs: '16px'
                      }
                    }}
                  >
                    <PublisherLikeButton
                      issueId={issueId ?? ''}
                      cantInquiry={cantInquiry}
                      setOpen={handleSnackbarOpen}
                    />
                  </Grid>
                </Grid>
              </Typography>
            </CardContent>
          </Grid>
        </Card>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={cantInquiry ?? ''}
      />
    </Grid>
  );
};

export default PublisherIssueCard;
